
import { useState, useEffect, useContext } from "react";
import {
  FaUtensils,
  FaTable,
  FaClipboardList,
  FaChartLine,
  FaCog,
  FaTachometerAlt,
  FaFileAlt,
  FaSun,
  FaMoon,
  FaBox,
  FaBars,
  FaTimes,
  FaUserCircle,
  FaMobileAlt,
} from "react-icons/fa";
import Menus from "./Menus";
import Tables from "./Tables";
import Orders from "./Orders";
import Analytics from "./Analytics";
import Settings from "./Settings";
import Dashboard from "./Dashboard";
import Reports from "./Reports";
import { getRestaurantProfile, updateRestaurantProfile } from "../utils/api";
import { fetchCaptains, createCaptain, setCurrentCaptain } from "../utils/api";
import { useNavigate } from "react-router-dom";
import AuthContext from "../contexts/AuthContext";
import { useAuth } from "../contexts/AuthContext";

const sidebarItems = [
  { name: "Dashboard", icon: <FaTachometerAlt /> },
  { name: "Menus", icon: <FaUtensils /> },
  { name: "Tables", icon: <FaTable /> },
  { name: "Orders", icon: <FaClipboardList /> },
  { name: "Analytics", icon: <FaChartLine /> },
  { name: "Reports", icon: <FaFileAlt /> },
  { name: "Settings", icon: <FaCog /> },
];

export default function AdminHome() {
  const [active, setActive] = useState("Dashboard");
  const { restaurantId } = useContext(AuthContext);
  const { logout } = useAuth();
  const [restaurant, setRestaurant] = useState({ id: "", name: "", storeInventoryEnabled: true, mobileViewEnabled: true });
  const [showEditModal, setShowEditModal] = useState(false);
  const [editName, setEditName] = useState("");
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState("");
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [captains, setCaptains] = useState([]);
  const [loadingCaptains, setLoadingCaptains] = useState(false);
  const [captainModalOpen, setCaptainModalOpen] = useState(false);
  const [newCaptainName, setNewCaptainName] = useState("");
  const [currentCaptainId, setCurrentCaptainId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const root = window.document.documentElement;
    const oldTheme = theme === "dark" ? "light" : "dark";
    root.classList.remove(oldTheme);
    root.classList.add(theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  useEffect(() => {
    if (!localStorage.getItem("adminToken")) {
      navigate("/");
    }
  }, [navigate]);

  // Close sidebar on mobile when screen size changes
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserDropdown && !event.target.closest('.user-dropdown')) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserDropdown]);

  useEffect(() => {
    async function fetchProfile() {
      if (!restaurantId) return;

      // fetch captains as well
      setLoadingCaptains(true);
      setLoadingProfile(true);
      setProfileError("");
      try {
        const data = await getRestaurantProfile(restaurantId);
        setRestaurant({
          id: data.id || restaurantId,
          name: data.name || "",
          storeInventoryEnabled: data.storeInventoryEnabled !== false,
          mobileViewEnabled: data.mobileViewEnabled !== false, // Default to true if not set
        });
        // set current captain if present in restaurant profile
        if (data.currentCaptainId) setCurrentCaptainId(data.currentCaptainId);
        // load captains list
        try {
          const caps = await fetchCaptains(restaurantId);
          setCaptains(Array.isArray(caps) ? caps : []);
        } catch (capErr) {
          console.warn("Failed to load captains:", capErr);
          setCaptains([]);
        }
      } catch (err) {
        setProfileError("Failed to load profile");
        console.error("Error loading profile:", err);
      }
      setLoadingProfile(false);
      setLoadingCaptains(false);
    }
    fetchProfile();
  }, [restaurantId]);

  // set current captain on server when user selects from dropdown
  async function handleSetCurrentCaptain(capId) {
    if (!restaurantId) return;
    try {
      await setCurrentCaptain(restaurantId, capId || null);
      setCurrentCaptainId(capId || null);
    } catch (err) {
      console.error("Failed to set current captain:", err);
      alert("Failed to set captain");
    }
  }

  async function handleCreateCaptain() {
    if (!restaurantId) {
      alert("No restaurant id");
      return;
    }
    if (!newCaptainName.trim()) return;
    try {
      const res = await createCaptain(restaurantId, newCaptainName.trim());
      // refresh captains list
      const caps = await fetchCaptains(restaurantId);
      setCaptains(Array.isArray(caps) ? caps : []);
      setNewCaptainName("");
      setCaptainModalOpen(false);
      // automatically select the newly created captain
      if (res && res.id) {
        await handleSetCurrentCaptain(res.id);
      }
    } catch (err) {
      console.error("Failed to create captain:", err);
      alert("Failed to create captain");
    }
  }

  function handleEditRestaurant() {
    setEditName(restaurant.name);
    setShowEditModal(true);
  }

  async function handleSaveRestaurant() {
    if (!restaurantId) {
      setProfileError("No restaurant ID found");
      return;
    }
    if (!editName.trim()) {
      setProfileError("Restaurant name cannot be empty");
      return;
    }

    setSavingProfile(true);
    setProfileError("");
    console.log("[AdminHome] Updating restaurant profile with:", {
      restaurantId,
      name: editName,
    });
    try {
      const result = await updateRestaurantProfile({
        restaurantId,
        name: editName.trim(),
      });
      if (result.success) {
        setRestaurant((prev) => ({ ...prev, name: editName.trim() }));
        setShowEditModal(false);
      } else {
        setProfileError("Failed to update profile");
      }
    } catch (err) {
      setProfileError(err.message || "Failed to update profile");
    }
    setSavingProfile(false);
  }

  async function handleLogout() {
    try {
      await logout();
      localStorage.clear();
      navigate("/");
    } catch (err) {
      console.error("Logout failed:", err);
      localStorage.clear();
      navigate("/");
    }
  }

  const handleSidebarItemClick = (itemName) => {
    setActive(itemName);
    // Close sidebar on mobile after item selection
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  const toggleTheme = () => {
    setTheme(theme === "light" ? "dark" : "light");
  };

  const toggleUserDropdown = () => {
    setShowUserDropdown(!showUserDropdown);
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <div className="flex min-h-screen bg-rose-50 dark:bg-slate-900 transition-colors duration-300">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Updated to appear from right on mobile */}
      <aside
        className={`fixed lg:static inset-y-0 right-0 z-50 w-64 bg-white dark:bg-slate-800 border-l lg:border-r dark:border-slate-700 p-3 sm:p-4 lg:p-6 flex flex-col gap-2 sm:gap-3 lg:gap-4 h-screen transition-transform duration-300 ${
          sidebarOpen ? "translate-x-0" : "translate-x-full lg:translate-x-0"
        }`}
      >
        {/* Mobile Close Button */}
        <button
          onClick={() => setSidebarOpen(false)}
          className="lg:hidden absolute top-4 right-4 p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <FaTimes className="text-xl" />
        </button>

        {loadingProfile ? (
          <div className="mb-6 sm:mb-8 flex items-center gap-2 sm:gap-3 bg-gradient-to-r from-rose-400 via-pink-300 to-orange-200 p-3 sm:p-4 rounded-lg shadow animate-pulse h-16 sm:h-20" />
        ) : (
          <div className="mb-6 sm:mb-8 flex flex-col gap-2 bg-gradient-to-r from-rose-400 via-pink-300 to-orange-200 p-3 sm:p-4 rounded-lg shadow text-white">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="font-bold text-base sm:text-lg flex items-center gap-2">
                {restaurant.name || "My Restaurant"}
                
              </div>
            </div>
            {profileError && (
              <div className="text-xs text-red-200 mt-2">{profileError}</div>
            )}
          </div>
        )}

        {sidebarItems.map((item) => (
          <button
            key={item.name}
            className={`flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base font-medium transition w-full ${
              active === item.name
                ? "bg-rose-100 text-primary dark:bg-slate-700 dark:text-white"
                : "text-gray-600 dark:text-gray-300 hover:bg-rose-100 dark:hover:bg-slate-700"
            }`}
            onClick={() => handleSidebarItemClick(item.name)}
          >
            <span className="text-lg sm:text-xl">{item.icon}</span> {item.name}
          </button>
        ))}

        {/* Inventory Button in Sidebar */}
        {restaurant.storeInventoryEnabled && (
          <button
            onClick={() => window.open("/admin/inventory", "_blank")}
            className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base font-medium transition w-full text-blue-600 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-slate-700"
          >
            <FaBox className="text-lg sm:text-xl" /> Inventory
          </button>
        )}
        
        {/* Mobile View button (opens the mobile view route) */}
        {restaurant.mobileViewEnabled && (
        <button
          onClick={() => navigate("/mobile", { state: { initial: "Home" } })}
          className="flex items-center gap-2 sm:gap-3 mt-2 px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base font-medium transition w-full text-purple-600 dark:text-purple-300 hover:bg-purple-100 dark:hover:bg-slate-700"
        >
          <FaMobileAlt className="text-lg sm:text-xl" /> Mobile View
        </button>
        )}
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-4 sm:p-6 lg:p-10 overflow-y-auto h-screen">
        {/* Mobile Header with Hamburger Icon next to User Circle */}
        <div className="lg:hidden flex items-center justify-between mb-4 sm:mb-6">
          <div className="flex items-center gap-2">
            {/* Captain compact control (mobile): label/button opens modal, small select */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCaptainModalOpen(true)}
                className="px-2 py-1 rounded-md bg-rose-100 text-rose-700 text-sm font-semibold"
              >
                Captain
              </button>
              <select
                value={currentCaptainId || ""}
                onChange={(e) => handleSetCurrentCaptain(e.target.value || null)}
                className="text-sm py-1 px-2 rounded border bg-white"
              >
                <option value="">Unassigned</option>
                {captains.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={toggleTheme}
              className="p-1.5 sm:p-2 rounded-full bg-gray-200 dark:bg-slate-700 hover:bg-gray-300 dark:hover:bg-slate-600 transition-colors"
            >
              {theme === "light" ? (
                <FaSun className="text-yellow-500 text-sm sm:text-base" />
              ) : (
                <FaMoon className="text-slate-300 text-sm sm:text-base" />
              )}
            </button>
            
            {/* Hamburger Menu Button - Moved next to User Circle */}
            <button
              onClick={toggleSidebar}
              className="p-1.5 sm:p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              <FaBars size={16} />
            </button>
            
            <div className="relative user-dropdown">
              <button
                onClick={toggleUserDropdown}
                className="p-1.5 sm:p-2 rounded-full bg-gray-200 dark:bg-slate-700 hover:bg-gray-300 dark:hover:bg-slate-600 transition-colors"
              >
                <FaUserCircle className="text-gray-600 dark:text-gray-300 text-sm sm:text-base" />
              </button>
              {showUserDropdown && (
                <div className="absolute right-0 mt-2 w-32 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-gray-200 dark:border-slate-600 z-50">
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg"
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Desktop Header */}
        <div className="hidden lg:flex items-center justify-between mb-6 sm:mb-8">
          <div className="flex items-center gap-2">
            <span className="text-xl">&#128204;</span>
            <span className="font-semibold text-lg text-gray-800 dark:text-gray-200">
              {restaurant.name || "My Restaurant"}
            </span>
          </div>

          <div className="flex items-center gap-2">
            {/* Captain control (desktop) */}
            <div className="flex items-center gap-3 mr-2">
              <button
                onClick={() => setCaptainModalOpen(true)}
                className="px-3 py-2 rounded-md bg-rose-100 text-rose-700 font-semibold"
              >
                Captain
              </button>
              <select
                value={currentCaptainId || ""}
                onChange={(e) => handleSetCurrentCaptain(e.target.value || null)}
                className="py-2 px-3 rounded border bg-white"
              >
                <option value="">Unassigned</option>
                {captains.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full bg-gray-200 dark:bg-slate-700 hover:bg-gray-300 dark:hover:bg-slate-600 transition-colors"
            >
              {theme === "light" ? (
                <FaSun className="text-yellow-500" />
              ) : (
                <FaMoon className="text-slate-300" />
              )}
            </button>
            
            <div className="relative user-dropdown">
              <button
                onClick={toggleUserDropdown}
                className="p-2 rounded-full bg-gray-200 dark:bg-slate-700 hover:bg-gray-300 dark:hover:bg-slate-600 transition-colors"
              >
                <FaUserCircle className="text-gray-600 dark:text-gray-300" />
              </button>
              {showUserDropdown && (
                <div className="absolute right-0 mt-2 w-32 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-gray-200 dark:border-slate-600 z-50">
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg"
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="min-h-0">
          {active === "Dashboard" && <Dashboard />}
          {active === "Menus" && <Menus />}
          {active === "Tables" && <Tables />}
          {active === "Orders" && <Orders restaurantId={restaurantId} />}
          {active === "Analytics" && <Analytics />}
          {active === "Reports" && <Reports />}
          {active === "Settings" && <Settings />}
        </div>
      </main>

      {/* Edit Restaurant Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-lg p-4 sm:p-6 w-full max-w-sm relative">
            <button
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-200 p-1"
              onClick={() => setShowEditModal(false)}
            >
              <FaTimes className="text-lg" />
            </button>
            <h3 className="text-lg font-bold mb-4 dark:text-white">
              Edit Restaurant Info
            </h3>
            <div className="mb-3">
              <label className="block text-sm font-semibold mb-1 dark:text-gray-300">
                Name
              </label>
              <input
                type="text"
                className="border dark:border-slate-600 px-3 py-2 rounded w-full focus:outline-none focus:ring-2 focus:ring-rose-300 bg-white dark:bg-slate-700 dark:text-white text-sm sm:text-base"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="Enter restaurant name"
              />
            </div>
            {profileError && (
              <div className="text-sm text-red-500 mb-3">{profileError}</div>
            )}
            <button
              onClick={handleSaveRestaurant}
              disabled={savingProfile || !editName.trim()}
              className={`w-full py-2 rounded font-medium text-white transition-colors text-sm sm:text-base ${
                savingProfile || !editName.trim()
                  ? "bg-gray-300 dark:bg-gray-600"
                  : "bg-gradient-to-r from-rose-400 to-pink-500 hover:from-rose-500 hover:to-pink-600"
              }`}
            >
              {savingProfile ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      )}

      {/* Add Captain Modal */}
      {captainModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4" onClick={() => setCaptainModalOpen(false)}>
          <div className="bg-white dark:bg-slate-800 rounded-lg p-4 sm:p-6 w-full max-w-sm relative" onClick={(e) => e.stopPropagation()}>
            <button
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-200 p-1"
              onClick={() => setCaptainModalOpen(false)}
            >
              <FaTimes className="text-lg" />
            </button>
            <h3 className="text-lg font-bold mb-4 dark:text-white">Add Captain</h3>
            <div className="mb-3">
              <label className="block text-sm font-semibold mb-1 dark:text-gray-300">Name</label>
              <input
                type="text"
                className="border dark:border-slate-600 px-3 py-2 rounded w-full focus:outline-none focus:ring-2 focus:ring-rose-300 bg-white dark:bg-slate-700 dark:text-white text-sm sm:text-base"
                value={newCaptainName}
                onChange={(e) => setNewCaptainName(e.target.value)}
                placeholder="Enter captain name"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleCreateCaptain}
                className="flex-1 w-full py-2 rounded font-medium text-white bg-gradient-to-r from-rose-400 to-pink-500 hover:from-rose-500 hover:to-pink-600"
              >
                Save
              </button>
              <button
                onClick={() => setCaptainModalOpen(false)}
                className="py-2 px-4 rounded font-medium border"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}